import React, { useState, useEffect } from 'react';

// reactstrap components
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from "reactstrap";

function SearchFilters({ filters, onFilterChange, onSearch, onClear, isLoading = false }) {
  const [criteria, setCriteria] = useState([]);

  // Available fields for search - only specific fields requested for deals
  const fieldOptions = [
    { value: '', label: 'Select Field' },
    { value: 'legalName', label: 'Legal Name' },
    { value: 'federalTaxId', label: 'Federal Tax ID' },
    { value: 'status', label: 'Status' },
    { value: 'createdBy', label: 'Created By' },
    { value: 'createdAt', label: 'Created At (Date Range)' }
  ];

  // Available operators - simplified for deals
  const operatorOptions = [
    { value: '=', label: 'equals' },
    { value: 'ILIKE', label: 'contains' }
  ];

  // Get appropriate operators for each field
  const getOperatorOptions = (field) => {
    if (field === 'status') {
      return [{ value: '=', label: 'equals' }];
    }
    if (field === 'createdAt') {
      return [{ value: 'BETWEEN', label: 'between' }]; // Hidden, but needed for logic
    }
    return operatorOptions;
  };

  // Pre-cargar un criterio vacío al montar para que el usuario tenga un campo listo
  useEffect(() => {
    if (criteria.length === 0) {
      const newId = `criteria-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setCriteria([{ id: newId, field: '', operator: 'ILIKE', value: '', value2: '' }]);
    }
  }, []);

  // Status options for deals - only specific statuses requested
  const statusOptions = [
    { value: '', label: 'Select Status' },
    { value: 'SCREENER', label: 'Screener' },
    { value: 'OPEN', label: 'Open' },
    { value: 'FUNDED', label: 'Funded' },
    { value: 'DECLINED', label: 'Declined' },
    { value: 'CANCELLED', label: 'Cancelled' },
    { value: 'CLOSED', label: 'Closed' }
  ];

  const addCriteria = () => {
    const newId = `criteria-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setCriteria(prevCriteria => [...prevCriteria, { id: newId, field: '', operator: 'ILIKE', value: '', value2: '' }]);
  };

  const removeCriteria = (id) => {
    setCriteria(prevCriteria => prevCriteria.filter(c => c.id !== id));
  };

  const updateCriteria = (id, field, value) => {
    console.log(`🔄 updateCriteria: ${id}, ${field} = "${value}"`);
    setCriteria(prevCriteria => {
      const updated = prevCriteria.map(c => 
        c.id === id ? { ...c, [field]: value } : c
      );
      console.log('📋 Updated criteria:', updated);
      return updated;
    });
  };

  const handleSearch = () => {
    console.log('🚀 handleSearch called');
    console.log('📋 Current criteria:', criteria);
    
    // Validate that we have at least one valid criterion
    const validCriteria = criteria.filter(c => {
      if (!c.field || c.field === '') return false;
      
      // For createdAt, check if at least one date is provided
      if (c.field === 'createdAt') {
        return (c.value && c.value.trim() !== '') || (c.value2 && c.value2.trim() !== '');
      }
      
      // For other fields, check if value is provided
      return c.value && c.value.trim() !== '';
    });
    
    console.log('✅ Valid criteria found:', validCriteria.length);
    
    if (validCriteria.length === 0) {
      console.log('❌ No valid criteria found - showing all deals');
      onFilterChange({});
      onSearch();
      return;
    }
    
    // Convert criteria to filters format
    const searchFilters = {};
    
    validCriteria.forEach(c => {
      console.log('🔍 Processing valid criterion:', c);
      
      // Special handling for status field - convert to array for API compatibility
      if (c.field === 'status') {
        searchFilters.status = [c.value.trim()];
      } else if (c.field === 'createdAt') {
        // Special handling for date range - convert to datetime format
        if (c.value) {
          // From date: add T00:00:00Z
          searchFilters.createdAtFrom = `${c.value}T00:00:00Z`;
        }
        if (c.value2) {
          // To date: add T23:59:59Z
          searchFilters.createdAtTo = `${c.value2}T23:59:59Z`;
        }
      } else {
        // Add the main field value
        searchFilters[c.field] = c.value.trim();
      }
      
      // Add the operator for this field
      searchFilters[`${c.field}_operator`] = c.operator;
      
      // Log appropriate format for each field type
      if (c.field === 'createdAt') {
        console.log(`✅ Added filter: ${c.field} From="${c.value}T00:00:00Z" To="${c.value2}T23:59:59Z" (operator: ${c.operator})`);
      } else {
        console.log(`✅ Added filter: ${c.field} = "${c.value.trim()}" (operator: ${c.operator})`);
      }
    });
    
    console.log('🎯 Final searchFilters object:', searchFilters);
    
    // Call the parent handlers
    console.log('📤 Calling onFilterChange with:', searchFilters);
    onFilterChange(searchFilters);
    
    console.log('📤 Calling onSearch with filters:', searchFilters);
    onSearch(searchFilters);
  };

  const handleClear = () => {
    const newId = `criteria-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setCriteria([{ id: newId, field: '', operator: 'ILIKE', value: '', value2: '' }]);
    onClear();
  };

  const hasValidCriteria = () => {
    const valid = criteria.some(c => {
      if (!c.field || c.field === '') return false;
      
      // For createdAt, check if at least one date is provided
      if (c.field === 'createdAt') {
        return (c.value && c.value.trim() !== '') || (c.value2 && c.value2.trim() !== '');
      }
      
      // For other fields, check if value is provided
      return c.value && c.value.trim() !== '';
    });
    console.log('🔍 hasValidCriteria check:', { criteria, valid });
    return valid;
  };

  const getValueInput = (criterion) => {
    const { field, value, value2 } = criterion;
    
    switch (field) {
      case 'status':
        return (
          <Input
            type="select"
            size="sm"
            value={value}
            onChange={(e) => updateCriteria(criterion.id, 'value', e.target.value)}
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        );
      
      case 'createdAt':
        return (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 mr-2">
              <small className="text-muted d-block mb-1">From:</small>
              <Input
                type="date"
                size="sm"
                value={value}
                onChange={(e) => updateCriteria(criterion.id, 'value', e.target.value)}
                placeholder="Start date"
              />
            </div>
            <div className="flex-grow-1">
              <small className="text-muted d-block mb-1">To:</small>
              <Input
                type="date"
                size="sm"
                value={value2}
                onChange={(e) => updateCriteria(criterion.id, 'value2', e.target.value)}
                placeholder="End date"
              />
            </div>
          </div>
        );

      case 'federalTaxId':
        // Máscara: 2 dígitos, guion automático y 7 dígitos (NN-NNNNNNN)
        return (
          <Input
            type="text"
            size="sm"
            placeholder="XX-XXXXXXX"
            maxLength="10"
            value={value}
            onChange={(e) => {
              const digits = String(e.target.value || '').replace(/\D/g, '').slice(0, 9);
              const masked = digits.length <= 2 ? digits : `${digits.slice(0, 2)}-${digits.slice(2)}`;
              updateCriteria(criterion.id, 'value', masked);
            }}
          />
        );
      
      default:
        return (
          <Input
            type="text"
            size="sm"
            placeholder="Enter value..."
            value={value}
            onChange={(e) => {
              console.log(`🔍 Input change for ${criterion.field}: "${e.target.value}"`);
              updateCriteria(criterion.id, 'value', e.target.value);
            }}
          />
        );
    }
  };

  return (
    <div>
      {/* Search Criteria Section */}
      <div className="mb-2">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 className="mb-0 text-muted small">
            <i className="ni ni-zoom-split-in mr-1"></i>
            Search Criteria
          </h6>
        </div>

        {/* Criteria Rows */}
        {criteria.length === 0 ? (
          <div className="text-center py-2 border rounded bg-light">
            <i className="ni ni-zoom-split-in text-muted" style={{ fontSize: '1rem' }}></i>
            <p className="text-muted mt-1 mb-0 small">All deals shown by default. Add criteria to filter.</p>
          </div>
        ) : (
          <div className="border rounded bg-light" style={{ maxWidth: '900px' }}>
            {/* Table Headers */}
            <div className="p-2 border-bottom bg-white">
              <Row>
                <Col md="4">
                  <small className="text-muted font-weight-bold">Field</small>
                </Col>
                <Col md="3">
                  <small className="text-muted font-weight-bold">Operator</small>
                </Col>
                <Col md="5">
                  <small className="text-muted font-weight-bold">Value</small>
                </Col>
              </Row>
            </div>
            
            {/* Criteria Rows */}
            <div className="p-2">
              {criteria.map((criterion, index) => (
                <Row key={criterion.id} className={`align-items-end ${index > 0 ? 'mt-1' : ''}`}>
                  <Col md="4">
                    <FormGroup className="mb-0">
                      <Input
                        type="select"
                        size="sm"
                        value={criterion.field}
                        onChange={(e) => {
                          const selectedField = e.target.value;
                          updateCriteria(criterion.id, 'field', selectedField);
                          
                          // Set appropriate default operator for each field
                          if (selectedField === 'status') {
                            updateCriteria(criterion.id, 'operator', '=');
                          } else if (selectedField === 'createdAt') {
                            updateCriteria(criterion.id, 'operator', 'BETWEEN');
                          } else {
                            updateCriteria(criterion.id, 'operator', 'ILIKE');
                          }
                          updateCriteria(criterion.id, 'value', '');
                          updateCriteria(criterion.id, 'value2', '');
                        }}
                      >
                        {fieldOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  {/* Hide operator column for createdAt */}
                  {criterion.field !== 'createdAt' && (
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Input
                          type="select"
                          size="sm"
                          value={criterion.operator}
                          onChange={(e) => updateCriteria(criterion.id, 'operator', e.target.value)}
                        >
                          {getOperatorOptions(criterion.field).map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  )}
                  {/* Adjust value column width based on field */}
                  <Col md={criterion.field === 'createdAt' ? "8" : "5"}>
                    <FormGroup className="mb-0">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          {getValueInput(criterion)}
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 ml-1"
                          onClick={() => removeCriteria(criterion.id)}
                          title="Remove criteria"
                        >
                          <i className="ni ni-fat-remove text-danger"></i>
                        </Button>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              ))}
            </div>
          </div>
        )}
      </div>

        {/* Add Criteria Button */}
        <div className="mb-2">
          <Button
            color="outline-primary"
            size="sm"
            onClick={addCriteria}
          >
            <i className="ni ni-fat-add mr-1"></i>
            Add Criteria
          </Button>
        </div>

                 {/* Action Buttons */}
         <div className="d-flex justify-content-end">
           <Button
             color="warning"
             className="mr-2"
             onClick={handleClear}
           >
             Clear
           </Button>
           <Button
             color="primary"
             onClick={handleSearch}
             disabled={criteria.length === 0 || isLoading || !hasValidCriteria()}
           >
             {isLoading ? 'Searching...' : `Search (${criteria.length} criteria)`}
           </Button>
         </div>
    </div>
  );
}

export default SearchFilters;